import { useRef, useState, useEffect } from "react";
import { Play, Pause } from "lucide-react";
import useScrollReveal from "../hooks/useScrollReveal";
import { useLanguage } from "../i18n/LanguageContext";
import useParallax from "../hooks/useParallax";

export default function VideoSection() {
  const [ref, isVisible] = useScrollReveal(0.05);
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const { t } = useLanguage();
  const parallaxY = useParallax(0.2);
  const v = t.video;

  useEffect(() => {
    if (isVisible && videoRef.current && !hasLoaded) {
      videoRef.current.src = "/advaltis-brand-video.mp4";
      videoRef.current.load();
      videoRef.current.play().then(() => {
        setIsPlaying(true);
        setHasLoaded(true);
      }).catch(() => {
        setHasLoaded(true);
      });
    }
  }, [isVisible, hasLoaded]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <section ref={ref} data-testid="video-section" className="relative h-[50vh] md:h-[60vh] overflow-hidden">
      <video
        ref={videoRef}
        muted
        loop
        playsInline
        preload="none"
        className="absolute inset-0 w-full h-full object-cover will-change-transform"
        style={{ transform: `translateY(${parallaxY - parallaxY * 0.6}px)`, top: "-10%", bottom: "-10%", height: "120%" }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1221] via-[#0B1221]/60 to-[#0B1221]/40" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B1221]/80 to-transparent" />

      <div
        className="relative h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-16"
        style={{ opacity: isVisible ? 1 : 0, transition: "opacity 1s ease-out" }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="h-px w-12 bg-[#D4AF37]" />
          <span className="text-[#D4AF37] text-xs uppercase tracking-widest">{v.label}</span>
        </div>
        <h3
          className="text-white text-2xl md:text-3xl max-w-lg leading-snug mb-6"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          {v.heading}
        </h3>
        <button
          data-testid="video-play-toggle"
          onClick={togglePlay}
          className="flex items-center gap-3 text-slate-300 hover:text-[#D4AF37] transition-colors duration-300 text-sm uppercase tracking-widest group"
        >
          <div className="w-10 h-10 border border-slate-600 group-hover:border-[#D4AF37] flex items-center justify-center transition-colors duration-300">
            {isPlaying ? <Pause size={14} /> : <Play size={14} />}
          </div>
          {isPlaying ? v.pause : v.play}
        </button>
      </div>
    </section>
  );
}
